// Copyright 2025 Sorama B.V.

import * as semver from "semver";

/**
 * Union type for supported CamIV64 device types
 */
export const CamIV64DeviceTypes = ["iV64", "iV64s", "iV64Ex"] as const;
export type CamIV64DeviceType = (typeof CamIV64DeviceTypes)[number];

/**
 * Represents a CamIV64 device, all properties are derived from the device ID
 */
export class CamIv64Device {
    /**
     * Unique identifier for the device, typically in the format "sorama-iv64ex-123456789"
     */
    public readonly id: string;

    constructor(id: string) {
        this.id = id;
    }
    /**
     * Returns the device type based on the device ID
     * @returns The CamIV64DeviceType or null if not recognized
     */
    public get type(): CamIV64DeviceType {
        return CamIV64Helpers.getDeviceType(this.id);
    }

    /**
     * Returns the serial number extracted from the device ID
     * @returns Serial number or "Unknown" if not found
     */
    public get serialNumber(): string {
        return CamIV64Helpers.getSerialNumber(this.id);
    }

    /**
     * Returns the device name formatted as "Sorama CAM {type} {serialNumber}"
     * @returns Formatted device name
     */
    public get name(): string {
        return CamIV64Helpers.getDeviceName(this.id);
    }
}

/**
 * Utility class for CamIV64 device operations
 */
export class CamIV64Helpers {
    // Regex patterns for device identification
    public static readonly DEVICE_SERIAL_NUMBER_PATTERN = /([0-9]{9})$/;
    public static readonly DEVICE_TYPE_PATTERN = /(iv64ex|iv64-ex|iv64s|iv64-s|iv64)(?=-[0-9]{9}$)/;

    /**
     * Minimum firmware version for iV64 devices to support SorX measurements.
     */
    public static readonly IV64_SORX_MIN_VERSION = "3.0.0";

    /**
     * Determines if a device supports SorX measurements based on device type and firmware version.
     * @returns `true` if SorX is supported, `false` if not, `null` if it cannot be determined (e.g. dev firmware)
     */
    public static supportsSorX(deviceType: CamIV64DeviceType, firmwareVersion: string): boolean | null {
        if (deviceType === "iV64s" || deviceType === "iV64Ex") return true;

        if (deviceType === "iV64") {
            const cleaned = semver.clean(firmwareVersion);
            if (cleaned) {
                return semver.gte(cleaned, this.IV64_SORX_MIN_VERSION);
            }
            // Non-semver version (dev builds) — cannot determine, caller should attempt SorX with fallback
            return null;
        }

        return false;
    }

    /**
     * Extracts the device type from a device ID
     * @param deviceId The device ID string
     * @returns The device type or null if not found
     */
    public static getDeviceType(deviceId: string): CamIV64DeviceType | null {
        const match = deviceId.match(this.DEVICE_TYPE_PATTERN);
        if (!match) return null;

        // Normalize and return readable device type format
        const deviceType = match[1].toLowerCase();
        if (deviceType === "iv64-ex" || deviceType === "iv64ex") return "iV64Ex";
        if (deviceType === "iv64-s" || deviceType === "iv64s") return "iV64s";
        if (deviceType === "iv64") return "iV64";

        return null;
    }

    /**
     * Extracts the serial number from a device ID using regex pattern
     * @param deviceId The device ID string
     * @returns The serial number or "Unknown" if not found
     */
    public static getSerialNumber(deviceId?: string): string {
        if (!deviceId) return "Unknown";
        const match = deviceId.match(this.DEVICE_SERIAL_NUMBER_PATTERN);
        return match ? match[1] : "Unknown";
    }

    /**
     * Gets the display name for a CamIV64 device
     * @param deviceId The device ID string
     * @returns Formatted device name with "Sorama" prefix, device type, and serial number
     */
    public static getDeviceName(deviceId?: string): string {
        if (!deviceId) return "Unknown Device";

        const deviceType = this.getDeviceType(deviceId);
        const serialNumber = this.getSerialNumber(deviceId);

        if (deviceType && serialNumber !== "Unknown") {
            return `Sorama CAM ${deviceType} ${serialNumber}`;
        }

        // Fallback to deviceId if type or serial number is not found
        return deviceId;
    }
}
